import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { partnersApi, contactsApi, tasksApi, type Task } from '@/lib/api';
import { mitHerkunft, type Herkunft } from '@/lib/herkunft';
import { AnsprechpartnerKarte, kontaktAus } from '@/components/Ansprechpartner';
import { Briefcase, Edit3, Mail, Plus, Star, Trash2, User } from 'lucide-react';
import {
  Angaben,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  EmptyState,
  Kontakt,
  Skeleton,
  Status,
  Tabs,
  TabsContent,
  formatDatum,
} from '@elbklar/ui';
import { CrmPageHeader } from '@/components/layout/CrmPageHeader';
import { ActivityTimeline } from '@/features/interactions/components/ActivityTimeline';
import { InteractionSheet } from '@/features/interactions/components/InteractionSheet';
import { TaskCard } from '@/features/tasks/components/TaskCard';
import { TaskSheet } from '@/features/tasks/components/TaskSheet';
import { useAufgabeErledigen } from '@/features/tasks/useAufgabeErledigen';
import type { PartnerType, Contact } from '@/types';

const partnerTypeLabels: Record<PartnerType, string> = {
  cleaning_company: 'Reinigungsfirma',
  netzwerkpartner: 'Netzwerkpartner',
  subcontractor: 'Subunternehmer',
  real_estate_agent: 'Immobilienmakler',
  architect: 'Architekt',
  other: 'Sonstiger',
};

export function PartnerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'contacts' | 'activities' | 'tasks' | 'emails'>('contacts');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showInteractionModal, setShowInteractionModal] = useState(false);
  // Aufgabe im Sheet bearbeiten, derselbe Dialog wie auf der Aufgabenseite.
  // `null` heisst zu.
  const [bearbeiteteAufgabe, setBearbeiteteAufgabe] = useState<Task | null>(null);
  const erledigen = useAufgabeErledigen();

  // Von dieser Seite aus geklickt: Kontakte tragen den Partner als Herkunft
  // mit, damit der Weg zurueck nach dem Bearbeiten hierher fuehrt und nicht
  // in die allgemeine Kontaktliste (siehe lib/herkunft.ts).
  const herkunft: Herkunft = { partnerId: id };

  const { data: partner, isPending } = useQuery({
    queryKey: ['partners', id],
    queryFn: () => partnersApi.get(id!),
    enabled: !!id,
  });

  const { data: contacts } = useQuery({
    queryKey: ['contacts', { partner_id: id }],
    queryFn: () => contactsApi.list({ partner_id: id }),
    enabled: !!id,
  });

  // Load tasks for this partner (no customer inheritance for partners)
  const { data: tasks } = useQuery({
    queryKey: ['tasks', 'entity', 'partner', id],
    queryFn: () => tasksApi.getForEntity('partner', id!, false),
    enabled: !!id && activeTab === 'tasks',
  });

  // Find primary contact
  const primaryContact = useMemo(() => {
    return contacts?.items?.find(c => c.is_primary);
  }, [contacts]);

  const deleteMutation = useMutation({
    mutationFn: () => partnersApi.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      navigate('/partners');
    },
  });

  const handleDelete = () => deleteMutation.mutateAsync();

  if (isPending) {
    return (
      <div className="space-y-4 pt-4">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-48 rounded-lg" />
        <Skeleton className="h-64 rounded-lg" />
      </div>
    );
  }

  if (!partner) {
    return (
      <>
        <CrmPageHeader title="Partner" zurueck={{ to: '/partners', label: 'Zur Partnerliste' }} />
        <EmptyState
          icon={Briefcase}
          title="Partner nicht gefunden"
          action={<Button onClick={() => navigate('/partners')}>Zur Partnerliste</Button>}
        />
      </>
    );
  }

  // Format address
  const addressParts = [
    partner.street,
    [partner.postal_code, partner.city].filter(Boolean).join(' '),
    partner.country !== 'Deutschland' ? partner.country : null,
  ].filter(Boolean);

  const status = `partner_${partner.status}`;

  return (
    <div className="space-y-6">
      <CrmPageHeader
        size="page"
        zurueck={{ to: '/partners', label: 'Zur Partnerliste' }}
        title={partner.name}
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
            <Status status={status} />
            {partner.partner_number && <span>Partner-Nr. <span className="t-mono">{partner.partner_number}</span></span>}
            {partner.assigned_user_name && (
              <span className="inline-flex items-center gap-1.5">
                <User size={14} strokeWidth={1.75} aria-hidden />
                {partner.assigned_user_name}
              </span>
            )}
          </span>
        }
        mehrAktionen={[
          { label: 'Bearbeiten', icon: Edit3, onSelect: () => navigate(`/partners/${id}/edit`) },
          { label: 'Partner löschen', icon: Trash2, tone: 'danger', onSelect: () => setShowDeleteModal(true) },
        ]}
      />

      {/* Am Handy: alle Ansprechpartner mit Anrufen und E-Mail zum Antippen */}
      <AnsprechpartnerKarte contacts={contacts?.items ?? []} herkunft={herkunft} className="md:hidden" />

      {/* Primary Contact */}
      {primaryContact && (
        <Card icon={Star} title="Primärer Ansprechpartner" className="max-md:hidden">
          <Kontakt darstellung="knoepfe" {...kontaktAus(primaryContact, herkunft)} primaer={false} />
        </Card>
      )}

      {/* Info Cards */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2" title="Kontaktdaten" icon={Mail}>
          {partner.email || partner.phone || partner.website || addressParts.length > 0 ? (
            <Kontakt
              email={partner.email}
              telefon={partner.phone}
              website={partner.website}
              adresse={addressParts.length > 0 && addressParts.map((part, index) => <div key={index}>{part}</div>)}
            />
          ) : (
            <p className="t-small text-muted-foreground">Keine Kontaktdaten hinterlegt</p>
          )}
        </Card>

        <Card title="Partnerdetails" icon={Briefcase}>
          <Angaben
            items={[
              { label: 'Partnertyp', value: partnerTypeLabels[partner.partner_type] ?? partner.partner_type },
              { label: 'Status', value: <Status status={status} /> },
              { label: 'Erstellt am', value: formatDatum(partner.created_at), mono: true },
              { label: 'Zuletzt aktualisiert', value: formatDatum(partner.updated_at), mono: true },
            ]}
          />
        </Card>
      </div>

      {/* Notes Section */}
      {partner.notes && (
        <Card title="Notizen">
          <p className="whitespace-pre-wrap">{partner.notes}</p>
        </Card>
      )}

      {/* Tabs for Contacts, Interactions and Tasks */}
      <Card flush>
        <Tabs
          label="Bereiche des Partners"
          className="[&>[role=tablist]]:px-5"
          value={activeTab}
          onChange={(v) => setActiveTab(v as typeof activeTab)}
          items={[
            { value: 'contacts', label: 'Kontakte', count: contacts?.total ?? 0 },
            { value: 'activities', label: 'Aktivitäten' },
            { value: 'tasks', label: 'Aufgaben', count: tasks?.length ?? 0 },
          ]}
        >
          <TabsContent value="contacts" className="p-5">
            <div className="mb-4 flex justify-end">
              <Button size="sm" icon={Plus} onClick={() => navigate(mitHerkunft('/contacts/new', herkunft))}>
                Kontakt anlegen
              </Button>
            </div>
            <DataTable<Contact>
              label="Kontakte"
              rows={contacts?.items ?? []}
              onRowClick={(contact) => navigate(mitHerkunft(`/contacts/${contact.id}`, herkunft))}
              empty="Ansprechpartner erscheinen hier, sobald einer angelegt ist."
              columns={[
                {
                  key: 'name',
                  header: 'Name',
                  mobil: 'titel',
                  render: (contact) => (
                    <span className="flex items-center gap-2">
                      <span className="font-medium">
                        {contact.first_name} {contact.last_name}
                      </span>
                      {contact.is_primary && <span className="t-label text-highlight">Primär</span>}
                    </span>
                  ),
                },
                { key: 'email', header: 'E-Mail', render: (c) => c.email || '–' },
                { key: 'phone', header: 'Telefon', mono: true, mobil: 'unter', render: (c) => c.phone || c.mobile || '–' },
                { key: 'role', header: 'Position', mobil: 'unter', render: (c) => c.role || '–' },
              ]}
            />
          </TabsContent>

          <TabsContent value="activities" className="p-5">
            <ActivityTimeline entityType="partner" entityId={id!} onAddInteraction={() => setShowInteractionModal(true)} />
          </TabsContent>

          <TabsContent value="tasks" className="p-5">
            <div className="mb-4 flex justify-end">
              <Button size="sm" icon={Plus} onClick={() => navigate(`/tasks/new?partner_id=${id}`)}>
                Aufgabe anlegen
              </Button>
            </div>
            {tasks && tasks.length > 0 ? (
              <div className="overflow-hidden rounded-lg border border-border">
                {/* Stift je Zeile, wie auf der Aufgabenseite. Nicht die ganze
                    Zeile: Sie ist `checkable`, und `ListRow` schliesst
                    `onClick` damit aus — die runde Checkbox wäre ein Klickziel
                    im Klickziel. */}
                {tasks.map((task) => (
                  <TaskCard key={task.id} task={task} onStatusChange={erledigen} onEdit={(aufgabe) => setBearbeiteteAufgabe(aufgabe)} />
                ))}
              </div>
            ) : (
              <p className="py-8 text-center text-muted-foreground">Aufgaben zu diesem Partner erscheinen hier, sobald eine angelegt ist.</p>
            )}
          </TabsContent>
        </Tabs>
      </Card>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={showDeleteModal}
        onOpenChange={setShowDeleteModal}
        title="Partner löschen?"
        description={`„${partner.name}“ wird endgültig gelöscht. Das lässt sich nicht rückgängig machen.`}
        confirmLabel="Partner löschen"
        tone="danger"
        onConfirm={handleDelete}
      />

      <InteractionSheet
        open={showInteractionModal}
        onOpenChange={setShowInteractionModal}
        title="Interaktion anlegen"
        description={`Zu ${partner.name}`}
        partnerId={id}
      />

      <TaskSheet
        open={!!bearbeiteteAufgabe}
        onOpenChange={(offen) => {
          if (!offen) setBearbeiteteAufgabe(null);
        }}
        task={bearbeiteteAufgabe}
        description={`Zu ${partner.name}`}
      />
    </div>
  );
}
